import React, { useId, useState } from "react";
import styles from "./TenantSetupModal.module.css";
import api from "../../services/api";
import Modal from "../Modal/Modal";
import { Button } from "../ui";
import { toast } from "react-toastify";
import { useAuth } from "../../auth/AuthContext";
import {
  getTenantName,
  getTenantSegment,
  mustCompleteOnboarding,
} from "../../auth/onboarding";
import { SEGMENT_OPTIONS, isValidSegment } from "../../constants/segments";
import {
  readTrialProduct,
  forgetTrialProduct,
} from "../../constants/trialProduct";

/**
 * Onboarding em 3 passos do tenant (nome, segmento, persona da IA e 1º serviço).
 *
 * Reusa o `Modal` base — que já traz overlay, ESC, foco no container,
 * `role="dialog"`/`aria-modal` e trava de rolagem do `<body>`.
 * O layout interno é header fixo + miolo rolável + rodapé fixo, para que o
 * botão de avançar nunca saia da viewport (o passo 1 é mais alto que a tela).
 *
 * `closeOnOverlayClick={false}`: são 3 passos de formulário (nome, segmento,
 * persona da IA e 1º serviço) sem rascunho persistido — um clique acidental no
 * overlay faria o usuário refazer tudo. ESC e o ✕ seguem fechando, porque são
 * deliberados.
 *
 * ── Dois modos, um componente ────────────────────────────────────────────────
 *
 * O mesmo wizard atende dois casos que parecem iguais e não são:
 *
 * - **Criação** (`mustCompleteOnboarding(user)`): o usuário NÃO tem tenant — é o caso
 *   de quem entrou por "Continuar com Google". Um único `POST /tenants/setup`
 *   cria `Tenant` + `TenantMember(OWNER)` + serviços numa transação, com plano
 *   de trial. É idempotente: 201 quando cria, 200 quando já havia tenant.
 * - **Edição** (o resto): o tenant existe — inclusive quando lhe falta o
 *   `segment`, que é o estado normal de quem se cadastrou por e-mail/senha.
 *   Segue o caminho histórico — `PATCH /tenants/update` + N × `POST /services`.
 *
 * Por que não usar `PATCH` sempre: sem tenant ele responde **400 "Negócio não
 * encontrado."**, o que deixava o usuário do Google preso num modal que não
 * salva. E por que não usar `POST /tenants/setup` sempre: ele cria o
 * `TenantMember(OWNER)` e atribui trial — rodá-lo sobre um tenant existente
 * seria transformar "completar cadastro" em "criar empresa".
 */
export default function TenantSetupModal({ onClose = () => {}, onSuccess }) {
  const { user, refreshUser } = useAuth();
  const titleId = useId();

  // Modo do wizard. Fixado no primeiro render de propósito: o `user` muda
  // (o `refreshUser` pós-save traz o tenant novo) e o modo NÃO pode mudar no
  // meio do fluxo, sob pena de o mesmo submit trocar de endpoint na retentativa.
  const [isCreating] = useState(() => mustCompleteOnboarding(user));

  // Pré-seleção pelo acessor canônico. O antigo fallback
  // `?? user?.empresaSegmento` saiu: esse campo só existia no perfil sintético
  // do `register()` do `AuthContext`, cujo único consumidor — o
  // `RegistrationWizard` — não é renderizado em rota nenhuma. Era um segundo
  // caminho para a mesma decisão, alimentado por código morto.
  const preselectedSegment = isValidSegment(getTenantSegment(user))
    ? getTenantSegment(user)
    : "";

  // Em modo EDIÇÃO o negócio já tem nome — e o campo nascia vazio. Como o nome
  // é obrigatório no submit, quem abria o wizard só para escolher o segmento
  // era forçado a redigitá-lo; digitando diferente, RENOMEAVA o negócio sem
  // perceber, porque o `PATCH /tenants/update` manda o campo do mesmo jeito.
  // Em modo criação continua vazio: não há nome a preservar.
  const preselectedName = isCreating ? "" : (getTenantName(user) ?? "");

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  //Estados do Formulário
  const [formData, setFormData] = useState({
    businessName: preselectedName,
    segment: preselectedSegment,
    aiPersona: "Um assistente prestativo e educado",
    aiTone: "Educada",
    services: [{ name: "", duration: "30", price: "" }],
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleServiceChange = (index, field, value) => {
    const newServices = [...formData.services];
    newServices[index][field] = value;
    setFormData({ ...formData, services: newServices });
  };

  /**
   * Serviços que efetivamente vão para a API.
   *
   * Linha sem nome é linha em branco, não serviço: antes desta função o wizard
   * postava `{ name: "", duration: 30, price: 0 }` para quem só clicava
   * "Finalizar" — um serviço fantasma que aparecia na agenda e no chatbot sem
   * ninguém ter digitado nada. `parseInt("") || 30` transformava campo vazio em
   * default silenciosamente.
   */
  const collectServices = (skip) => {
    if (skip) return [];
    return formData.services
      .filter((s) => s.name.trim().length > 0)
      .map((s) => ({
        name: s.name.trim(),
        duration: parseInt(s.duration, 10) || 30,
        price: parseFloat(s.price) || 0,
      }));
  };

  /**
   * Caminho de EDIÇÃO: aplica o formulário a um tenant que já existe.
   *
   * Extraído porque o modo criação também precisa dele — ver o tratamento do
   * 200 em `handleSubmit`.
   *
   * @returns {Promise<{ok: boolean, tenant: object|undefined}>}
   */
  const applyToExistingTenant = async (services) => {
    const res = await api.patch(`/tenants/update`, {
      name: formData.businessName.trim(),
      segment: formData.segment,
      aiPersona: formData.aiPersona,
      aiTone: formData.aiTone,
    });

    if (res.status !== 200 && res.status !== 201) {
      return { ok: false, tenant: undefined };
    }

    await Promise.all(services.map((s) => api.post("/services", s)));
    return { ok: true, tenant: res.data.tenant };
  };

  const handleSubmit = async ({ skipServices = false } = {}) => {
    if (!formData.segment) {
      toast.warning("Selecione o tipo do seu negócio");
      setStep(1);
      return;
    }
    if (!formData.businessName.trim()) {
      toast.warning("Informe o nome do seu negócio");
      setStep(1);
      return;
    }
    setLoading(true);
    try {
      const services = collectServices(skipServices);
      let tenant = null;

      if (isCreating) {
        // Uma única chamada transacional: tenant + membro OWNER + serviços.
        // `produto` viaja desde o clique no site (ver `constants/trialProduct`)
        // e decide qual plano de trial o backend atribui.
        const created = await api.post("/tenants/setup", {
          businessName: formData.businessName.trim(),
          segment: formData.segment,
          aiPersona: formData.aiPersona,
          aiTone: formData.aiTone,
          services,
          produto: readTrialProduct(),
        });
        forgetTrialProduct();

        if (created.status === 201) {
          // Criou. A resposta é o tenant cru, não embrulhado em `{ tenant }`.
          tenant = created.data;
        } else {
          // 200 = idempotente: o servidor JÁ tinha tenant para este usuário e,
          // por ser idempotente, DESCARTOU tudo o que foi digitado. Tratar isso
          // como sucesso é anunciar "Dados salvos com sucesso!" sobre um no-op
          // — o usuário preencheu três passos e nada mudou.
          //
          // Emendamos com o caminho de edição em vez de só trocar a copy: o
          // 200 é exatamente a situação que a edição resolve (o tenant existe),
          // e os valores acabaram de ser declarados pelo usuário com a intenção
          // de virarem a configuração do negócio. A alternativa — avisar "seu
          // negócio já existia" e parar — deixaria o formulário evaporar sem
          // oferecer nenhuma forma de aplicá-lo, que é a mesma classe de beco
          // sem saída que esta sessão veio eliminar.
          //
          // ⚠️ Assume o contrato: 200 significa que NADA foi gravado. Se o
          // backend passar a criar os serviços também no 200, o `POST /services`
          // daqui duplicaria — dano visível e reversível pela tela de Serviços,
          // ao contrário de um onboarding descartado em silêncio.
          const emenda = await applyToExistingTenant(services);
          if (!emenda.ok) return;
          tenant = emenda.tenant ?? created.data;
        }
      } else {
        const edited = await applyToExistingTenant(services);
        if (!edited.ok) return;
        tenant = edited.tenant;
      }

      // O contexto é o dono do `user` (INV-F2: ninguém escreve `localStorage`
      // de sessão à mão). Refrescamos ANTES do `onSuccess` para que a guarda de
      // onboarding já enxergue o tenant novo quando a tela chamadora navegar —
      // sem isso ela ricochetearia de volta para `/onboarding`.
      await refreshUser();

      toast.success("Dados salvos com sucesso!");
      if (onSuccess) {
        onSuccess(tenant);
      }
    } catch (error) {
      // O backend responde `{ error, details }`. Ler `.message` — como se fazia
      // aqui — devolvia sempre `undefined`, e o usuário via "Erro interno no
      // servidor" para qualquer causa, inclusive validação de campo.
      const mensagemServidor =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Erro interno no servidor";
      console.error("❌ Erro ao salvar configuração:", error.response?.data);
      toast.error(`Erro: ${mensagemServidor}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      width="34rem"
      titleId={titleId}
      closeOnOverlayClick={false}
    >
      <div className={styles.wizard}>
        <div className={styles.header}>
          <div className={styles.headerText}>
            <h2 id={titleId} className={styles.title}>
              Configurar meu Agendador
            </h2>
            <span
              className={styles.stepIndicator}
              aria-label="Progresso de Cadastro"
            >
              Passo {step} de 3
            </span>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Fechar configuração"
          >
            &times;
          </button>
        </div>

        <div className={styles.content}>
          {/* PASSO 1: DADOS DO NEGÓCIO */}
          {step === 1 && (
            <div className={styles.step}>
              <h3>Sobre sua Empresa</h3>
              <label>Nome do Negócio</label>
              <input
                name="businessName"
                placeholder="Ex: Barbearia Nexus"
                value={formData.businessName}
                onChange={handleChange}
              />
              <label>Tipo de Negócio</label>
              <div className={styles.segmentGrid}>
                {SEGMENT_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`${styles.segmentCard}${formData.segment === opt.value ? ` ${styles.selected}` : ""}`}
                    onClick={() =>
                      setFormData({ ...formData, segment: opt.value })
                    }
                    aria-pressed={formData.segment === opt.value}
                  >
                    <span className={styles.segmentIcon}>{opt.icon}</span>
                    <p className={styles.segmentName}>{opt.name}</p>
                    <p className={styles.segmentDesc}>{opt.desc}</p>
                    <div className={styles.segmentBadges}>
                      {opt.badges.map((badge) => (
                        <span key={badge} className={styles.segmentBadge}>
                          {badge}
                        </span>
                      ))}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* PASSO 2: PERSONALIDADE DE IA */}
          {step === 2 && (
            <div className={styles.step}>
              <h3>Personalidade da IA</h3>
              <label>Como a IA deve se apresentar?</label>
              <textarea
                name="aiPersona"
                placeholder="Ex: você é o atendente virtual da Nexus, seja amigável e use gírias de barbeiro."
                value={formData.aiPersona}
                onChange={handleChange}
              />
              <label>Tom de Voz</label>
              <select
                name="aiTone"
                value={formData.aiTone}
                onChange={handleChange}
              >
                <option value="Objetiva">Objetiva (Direto ao ponto)</option>
                <option value="Educada">Educada (Cordial e formal)</option>
                <option value="Descontraída">
                  Descontraída (Usa emojis e gírias)
                </option>
              </select>
            </div>
          )}
          {/*PASSO 3: PRIMEIRO SERVIÇO */}
          {step === 3 && (
            <div className={styles.step}>
              <h3>Cadastre seu primeiro serviço</h3>
              <p className={styles.stepHint}>
                Pode deixar para depois — dá para cadastrar serviços a qualquer
                momento em Serviços.
              </p>
              {formData.services.map((service, index) => (
                <div key={index} className={styles.serviceRow}>
                  <input
                    placeholder="Nome (Ex: Corte de Cabelo)"
                    value={service.name}
                    onChange={(e) =>
                      handleServiceChange(index, "name", e.target.value)
                    }
                  />
                  <input
                    placeholder="30 Minutos"
                    type="number"
                    value={service.duration}
                    onChange={(e) =>
                      handleServiceChange(index, "duration", e.target.value)
                    }
                  />
                  <input
                    placeholder="R$"
                    value={service.price}
                    onChange={(e) =>
                      handleServiceChange(index, "price", e.target.value)
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </div>
        <div className={styles.footer}>
          {step > 1 && (
            <Button variant="ghost" onClick={() => setStep(step - 1)}>
              Voltar
            </Button>
          )}
          {step < 3 ? (
            <Button
              variant="primary"
              className={styles.primary}
              onClick={() => {
                if (step === 1 && !formData.businessName.trim()) {
                  toast.warning("Informe o nome do seu negócio para continuar");
                  return;
                }
                if (step === 1 && !formData.segment) {
                  toast.warning(
                    "Selecione o tipo do seu negócio para continuar",
                  );
                  return;
                }
                setStep(step + 1);
              }}
            >
              Próximo
            </Button>
          ) : (
            <>
              {/* Cadastrar serviço não pode ser condição para ter conta: sem
                  esta saída, quem ainda não sabe o preço fica preso no passo 3
                  (ou inventa um serviço em branco só para escapar). */}
              <Button
                variant="ghost"
                onClick={() => handleSubmit({ skipServices: true })}
                disabled={loading}
              >
                Pular e concluir
              </Button>
              <Button
                variant="primary"
                className={styles.primary}
                onClick={() => handleSubmit()}
                isLoading={loading}
              >
                {loading ? "Salvando..." : "Finalizar Configuração"}
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
